// REST API client singleton

import { authState } from './auth.js';
import { router } from './router.js';

/**
 * Get the backend API base URL
 * Electron: from preload script. Web: from PUBLIC_API_URL at build time.
 * @returns {string} Backend base URL
 */
export function getBackendUrl() {
  if (window.rpgishElectron?.backendUrl) {
    return window.rpgishElectron.backendUrl;
  }
  return process.env.PUBLIC_API_URL || '';
}

/**
 * Get full URL for an image path
 * @param {string|null|undefined} url - The URL or path to transform
 * @returns {string|null} Full URL for the image
 */
export function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return getBackendUrl() + url;
}

/**
 * API client class for making authenticated requests
 */
class ApiClient {
  constructor() {
    /** @type {string} */
    this.baseUrl = getBackendUrl();
  }

  /**
   * Make an authenticated API request
   * @param {string} path - API endpoint path
   * @param {RequestInit} [options] - Fetch options
   * @returns {Promise<Response>}
   */
  async fetch(path, options = {}) {
    const url = `${this.baseUrl}${path}`;
    const token = await authState.getAccessToken();

    const headers = new Headers(options.headers || {});
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 403) {
      router.navigate('/access-denied');
    }

    return response;
  }

  /**
   * Make a GET request and return JSON
   * @template T
   * @param {string} path - API endpoint path
   * @returns {Promise<T>}
   */
  async get(path) {
    const response = await this.fetch(path);
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Make a POST request and return JSON
   * @template T
   * @param {string} path - API endpoint path
   * @param {object} [data] - Request body
   * @returns {Promise<T>}
   */
  async post(path, data) {
    /** @type {RequestInit} */
    const options = { method: 'POST' };
    if (data) options.body = JSON.stringify(data);
    const response = await this.fetch(path, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }

  /**
   * Make a PUT request and return JSON
   * @template T
   * @param {string} path - API endpoint path
   * @param {object} [data] - Request body
   * @returns {Promise<T>}
   */
  async put(path, data) {
    /** @type {RequestInit} */
    const options = { method: 'PUT' };
    if (data) options.body = JSON.stringify(data);
    const response = await this.fetch(path, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Make a PATCH request and return JSON
   * @template T
   * @param {string} path - API endpoint path
   * @param {object} [data] - Request body
   * @returns {Promise<T>}
   */
  async patch(path, data) {
    /** @type {RequestInit} */
    const options = { method: 'PATCH' };
    if (data) options.body = JSON.stringify(data);
    const response = await this.fetch(path, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Make a DELETE request
   * @param {string} path - API endpoint path
   * @returns {Promise<void>}
   */
  async delete(path) {
    const response = await this.fetch(path, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Upload a file
   * @param {string} path - API endpoint path
   * @param {FormData} formData - Form data with file
   * @returns {Promise<object>}
   */
  async upload(path, formData) {
    const url = `${this.baseUrl}${path}`;
    const token = await authState.getAccessToken();

    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    // Don't set Content-Type - let browser set it with boundary for multipart

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}

// Singleton instance with domain-specific methods
class Api extends ApiClient {
  /**
   * Get backend URL for direct use
   * @returns {string}
   */
  getBackendUrl() {
    return this.baseUrl;
  }

  /**
   * Get full URL for an image path
   * @param {string|null|undefined} url - The URL or path to transform
   * @returns {string|null}
   */
  getImageUrl(url) {
    return getImageUrl(url);
  }

  // ========== User / Auth ==========

  /**
   * Get the current user's profile
   * @returns {Promise<object>}
   */
  async getMe() {
    return this.get('/api/me');
  }

  // ========== Games ==========

  /**
   * List user's games
   * @returns {Promise<object[]>}
   */
  async listGames() {
    return this.get('/api/games');
  }

  /**
   * Create a new game
   * @param {object} data - Game creation data
   * @returns {Promise<object>}
   */
  async createGame(data) {
    return this.post('/api/games', data);
  }

  /**
   * Join a game with a character
   * @param {string} gameId - Game ID
   * @param {string} characterId - Character ID
   * @returns {Promise<object>}
   */
  async joinGame(gameId, characterId) {
    return this.post(`/api/games/${gameId}/join`, { characterId });
  }

  /**
   * Delete a game
   * @param {string} gameId - Game ID
   * @returns {Promise<void>}
   */
  async deleteGame(gameId) {
    return this.delete(`/api/games/${gameId}`);
  }

  // ========== Staff Games ==========

  /**
   * List all games (staff)
   * @returns {Promise<Array<import('../types/jsdoc-types.js').Game>>}
   */
  async listAllGames() {
    return this.get('/api/staff/games');
  }

  /**
   * Delete a game as admin (no owner check)
   * @param {string} gameId - Game ID
   * @returns {Promise<void>}
   */
  async deleteGameAdmin(gameId) {
    return this.delete(`/api/staff/games/${gameId}`);
  }

  /**
   * Delete all games (staff)
   * @returns {Promise<void>}
   */
  async deleteAllGames() {
    return this.delete('/api/staff/games');
  }

  /**
   * Get one page of game events for the staff events viewer.
   * @param {string} gameId
   * @param {{ since?: number, limit?: number }} [opts]
   * @returns {Promise<{ events: Array<object>, hasMore: boolean, latestEventId: number }>}
   */
  async getStaffGameEvents(gameId, { since, limit } = {}) {
    const params = new URLSearchParams();
    if (since != null) params.set('since', String(since));
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString();
    return this.get(`/api/staff/games/${gameId}/events${qs ? `?${qs}` : ''}`);
  }

  // ========== Invitations ==========

  /**
   * Get user's pending game invitations
   * @returns {Promise<object[]>}
   */
  async getMyInvitations() {
    return this.get('/api/me/invitations');
  }

  // ========== Friends ==========

  /**
   * Get user's friends list
   * @returns {Promise<object[]>}
   */
  async getFriends() {
    return this.get('/api/friends');
  }

  /**
   * Remove a friend (deletes the bidirectional friendship).
   * @param {string} friendUserId
   */
  async removeFriend(friendUserId) {
    return this.delete(`/api/friends/${encodeURIComponent(friendUserId)}`);
  }

  /**
   * Get or create friend invite code
   * @returns {Promise<{code: string, url: string}>}
   */
  async getFriendInviteCode() {
    return this.get('/api/friends/invite-code');
  }

  /**
   * Accept a friend invitation
   * @param {string} code - Invitation code
   * @returns {Promise<object>}
   */
  async acceptFriendInvitation(code) {
    return this.post(`/api/friends/accept/${encodeURIComponent(code)}`, {});
  }

  /**
   * Search users by username prefix.
   * @param {string} q
   * @returns {Promise<Array<{id: string, username: string, email_hash: string, picture?: string, status: 'add'|'request_sent'|'request_received'|'already_friends'}>>}
   */
  async searchUsers(q) {
    return this.get(`/api/users/search?q=${encodeURIComponent(q)}`);
  }

  /**
   * List incoming + outgoing pending friend requests.
   * @returns {Promise<object[]>}
   */
  async listFriendRequests() {
    return this.get('/api/friends/requests');
  }

  /**
   * Send a friend request.
   * @param {string} toUserId
   * @param {string} [message]
   * @returns {Promise<{status: string, requestId?: string}>}
   */
  async sendFriendRequest(toUserId, message) {
    return this.post('/api/friends/requests', { toUserId, message });
  }

  /** @param {string} requestId */
  async acceptFriendRequest(requestId) {
    return this.post(`/api/friends/requests/${requestId}/accept`, {});
  }

  /** @param {string} requestId */
  async rejectFriendRequest(requestId) {
    return this.post(`/api/friends/requests/${requestId}/reject`, {});
  }

  /** @param {string} requestId */
  async cancelFriendRequest(requestId) {
    return this.delete(`/api/friends/requests/${requestId}`);
  }

  /**
   * Map of friendUserId -> 'online'|'offline' for the caller's friends.
   * @returns {Promise<Record<string, 'online'|'offline'>>}
   */
  async getFriendsPresence() {
    return this.get('/api/friends/presence');
  }

  // ========== Notifications ==========

  /**
   * @param {{unread?: boolean, limit?: number}} [opts]
   * @returns {Promise<{unreadCount: number, notifications: object[]}>}
   */
  async getNotifications(opts = {}) {
    const params = new URLSearchParams();
    if (opts.unread) params.set('unread', 'true');
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/api/notifications${qs}`);
  }

  async markAllNotificationsRead() {
    return this.post('/api/notifications/read-all', {});
  }

  // ========== Characters ==========

  /**
   * Get user's characters
   * @returns {Promise<object[]>}
   */
  async getCharacters() {
    return this.get('/api/characters');
  }

  /**
   * Get a specific character
   * @param {string} characterId - Character ID
   * @returns {Promise<object>}
   */
  async getCharacter(characterId) {
    return this.get(`/api/characters/${characterId}`);
  }

  /**
   * Create a new character
   * @param {object} data - Character creation data
   * @returns {Promise<object>}
   */
  async createCharacter(data) {
    return this.post('/api/characters', data);
  }

  /**
   * Delete a character
   * @param {string} characterId - Character ID
   * @returns {Promise<void>}
   */
  async deleteCharacter(characterId) {
    return this.delete(`/api/characters/${characterId}`);
  }

  // ========== Adventures ==========

  /**
   * Get marketplace adventures
   * @returns {Promise<object[]>}
   */
  async getMarketplaceAdventures() {
    return this.get('/api/market/adventures');
  }

  // ========== Rules (Classes, Backgrounds, Species, Feats) ==========

  /**
   * Get list of available classes
   * @returns {Promise<object[]>}
   */
  async getClasses() {
    return this.get('/api/rules/classes');
  }

  /**
   * Get class creation choices
   * @param {string} classSlug - Class slug
   * @returns {Promise<object>}
   */
  async getClassCreationChoices(classSlug) {
    return this.get(`/api/rules/classes/${classSlug}/creation-choices`);
  }

  /**
   * Get list of available backgrounds
   * @returns {Promise<object[]>}
   */
  async getBackgrounds() {
    return this.get('/api/rules/backgrounds');
  }

  /**
   * Get background creation choices
   * @param {string} backgroundSlug - Background slug
   * @returns {Promise<object>}
   */
  async getBackgroundCreationChoices(backgroundSlug) {
    return this.get(`/api/rules/backgrounds/${backgroundSlug}/creation-choices`);
  }

  /**
   * Get list of available species
   * @returns {Promise<object[]>}
   */
  async getSpecies() {
    return this.get('/api/rules/species');
  }

  /**
   * Get species creation choices
   * @param {string} speciesSlug - Species slug
   * @returns {Promise<object>}
   */
  async getSpeciesCreationChoices(speciesSlug) {
    return this.get(`/api/rules/species/${speciesSlug}/creation-choices`);
  }

  /**
   * Get list of available feats
   * @returns {Promise<object[]>}
   */
  async getFeats() {
    return this.get('/api/rules/feats');
  }

  /**
   * Get feat creation choices
   * @param {string} featSlug - Feat slug
   * @param {Record<string, string>} [params] - Optional query parameters
   * @returns {Promise<object>}
   */
  async getFeatCreationChoices(featSlug, params) {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/api/rules/feats/${featSlug}/creation-choices${query}`);
  }

  // ========== Equipment ==========

  /**
   * Get list of all equipment. Merges equipmentJson into the top level
   * so callers can access definition fields (cost, weapon, armor, etc.)
   * directly alongside metadata (id, published, version).
   * @returns {Promise<object[]>}
   */
  async getEquipment() {
    const items = await this.get('/api/equipment');
    return items.map((/** @type {any} */ item) => ({
      ...item.equipmentJson,
      id: item.id,
      slug: item.slug,
      published: item.published,
      version: item.version,
    }));
  }

  // ========== User Account ==========

  /**
   * Validate an invitation code
   * @param {string} code - Invitation code
   * @returns {Promise<object>}
   */
  async validateInvitationCode(code) {
    return this.get(`/api/invitations/${encodeURIComponent(code)}`);
  }

  /**
   * Create a new user account
   * @param {object} data - Account creation data
   * @returns {Promise<object>}
   */
  async createAccount(data) {
    return this.post('/api/users', data);
  }
}

export const api = new Api();

/** @type {string} CDN base URL for d3-assets images (includes CDN ID) */
let _cdnBaseUrl = '';

/**
 * Get the CDN base URL for constructing image URLs.
 * Pattern: {cdnURL}/{cdnID}
 */
export function getCDNBaseUrl() {
  return _cdnBaseUrl;
}

/**
 * Set CDN config from /api/config response.
 * @param {string} cdnURL - CDN URL (e.g., https://cdn.assets.dicedriven.com)
 * @param {string} cdnID - App CDN ID (e.g., j5e1a1DRqnoK)
 */
export function setCDNConfig(cdnURL, cdnID) {
  _cdnBaseUrl = `${cdnURL.replace(/\/$/, '')}/${cdnID}`;
}

/**
 * Fetch configuration from the backend
 * This includes d3-auth settings and other runtime config
 * @returns {Promise<object>}
 */
export async function fetchConfig() {
  const backendUrl = getBackendUrl();

  const response = await fetch(`${backendUrl}/api/config`);

  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`);
  }

  const config = await response.json();

  // Validate required d3-auth fields
  if (!config.d3AuthBaseURL) {
    throw new Error('Missing d3AuthBaseURL in backend config');
  }
  if (!config.d3AuthFrontendURL) {
    throw new Error('Missing d3AuthFrontendURL in backend config');
  }
  if (!config.d3AuthClientID) {
    throw new Error('Missing d3AuthClientID in backend config');
  }
  if (!config.d3AuthAudience) {
    throw new Error('Missing d3AuthAudience in backend config');
  }

  return config;
}
